import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/sched";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sparkles, BellOff, Send } from "lucide-react";
import { toast } from "sonner";

const fill = (s, p) => s.replace(/\{(\w+)\}/g, (_, k) => p?.[k] ?? "");

export function useWaitlistEvents(t) {
    const since = useRef(new Date().toISOString());
    const qc = useQueryClient();
    useEffect(() => {
        const id = setInterval(async () => {
            try {
                const { data } = await api.get("/waitlist/events", { params: { since: since.current } });
                if (data.length) {
                    since.current = data[0].created_at;
                    data.reverse().forEach((e) => toast(t("wl.title"), { description: fill(t(`wl.events.${e.type}`), e.params) }));
                    qc.invalidateQueries({ queryKey: ["appointments"] });
                    qc.invalidateQueries({ queryKey: ["waitlist-gaps"] });
                }
            } catch { /* ignore */ }
        }, 15000);
        return () => clearInterval(id);
    }, [t, qc]);
}

export function useGaps(dateStr, enabled) {
    return useQuery({ queryKey: ["waitlist-gaps", dateStr], queryFn: async () => (await api.get("/waitlist/gaps", { params: { date: dateStr } })).data, enabled, refetchInterval: 60000, staleTime: 30000 });
}

export function GapBlock({ gap, top, height, t, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    if (height < 24 || gap.muted) return null;
    const n = gap.matches.length;
    const act = async (path, body, msg) => {
        setBusy(true);
        try { await api.post(path, body); toast.success(msg); setOpen(false); onChanged?.(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const body = { employee_id: gap.employee_id, start: gap.start, end: gap.end };
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button onClick={(e) => e.stopPropagation()} data-testid={`gap-${gap.employee_id}-${fmtTime(gap.start)}`}
                    className={`absolute left-1 right-1 flex flex-col items-start justify-center rounded-lg border border-dashed px-2 text-left text-[10px] leading-tight transition-colors duration-200 ${n ? "border-accent/60 bg-accent/5 text-accent hover:bg-accent/10" : "border-border/60 text-muted-foreground/70 hover:bg-secondary/40"}`}
                    style={{ top, height }}>
                    <span className="font-semibold uppercase tracking-wide">{gap.minutes} {t("wl.available")}</span>
                    {n > 0 && height > 36 && <span className="flex items-center gap-1"><Sparkles className="h-3 w-3" />{n} {t("wl.matches")}</span>}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3" onClick={(e) => e.stopPropagation()} data-testid="gap-popover">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{fmtTime(gap.start)}–{fmtTime(gap.end)} · {gap.minutes} min</p>
                {n === 0 ? <p className="mt-2 text-sm text-muted-foreground">{t("wl.noMatches")}</p> : (
                    <ol className="mt-2 space-y-1.5">
                        {gap.matches.map((m, i) => (
                            <li key={m.entry_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`gap-match-${i}`}>
                                <span><span className="text-muted-foreground">{i + 1}.</span> {m.customer}<span className="ml-1 text-xs text-muted-foreground">{m.services.join(" + ")}</span></span>
                                <span className="flex items-center gap-1.5"><b className="text-accent">{m.score}%</b>
                                    <Button size="icon" variant="ghost" className="h-6 w-6" disabled={busy} title={t("wl.offerTo")} onClick={() => act("/waitlist/gaps/offer", { ...body, entry_id: m.entry_id }, t("wl.offerSentToast"))} data-testid={`gap-offer-${i}`}><Send className="h-3.5 w-3.5" /></Button></span>
                            </li>
                        ))}
                    </ol>
                )}
                <Button variant="ghost" size="sm" className="mt-3 w-full justify-start rounded-full text-xs text-muted-foreground" disabled={busy} onClick={() => act("/waitlist/gaps/mute", body, t("common.saved"))} data-testid="gap-mute"><BellOff className="mr-1 h-3.5 w-3.5" />{t("wl.doNotOffer")}</Button>
            </PopoverContent>
        </Popover>
    );
}

export function HoldBlock({ a, top, height, t }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(id); }, []);
    const left = Math.max(0, Math.round((new Date(a.expires_at) - now) / 60000));
    return (
        <div data-testid={`hold-${a.id}`} className="absolute left-1 right-1 overflow-hidden rounded-lg border-2 border-dashed px-2 py-1 text-[10px] leading-tight"
            style={{ top, height: Math.max(height, 22), borderColor: a.employee_color, background: `${a.employee_color}22`, color: "inherit" }}>
            <p className="truncate font-bold uppercase tracking-wide">{t("wl.hold")}</p>
            {height > 30 && <p className="truncate">{a.customer_name} · {t("wl.holdSub")}</p>}
            {height > 44 && <p className="truncate opacity-70">{t("wl.expires")} {fmtTime(a.expires_at)} · {left} min {t("wl.remaining")}</p>}
        </div>
    );
}
